export const BOOKING_SLUG = 'infiteq-offer'
export const BOOKING_ACCENT = '#2B5AFF'
export const BOOKING_MAX_WIDTH = 1280

const WIDGET_SRC = 'https://infiteq.app/widget/infiteq-booking.iife.js'
// The widget otherwise derives its API base from the script origin, which serves the SPA shell, not the API.
const BOOKING_API_URL = 'https://api.infiteq.app'

type BookingRenderOptions = {
  target: HTMLElement
  slug: string
  meetingType?: string
  apiUrl?: string
  accent?: string
  theme?: string
}

type BookingWindow = Window & {
  InfiteqBooking?: { render: (opts: BookingRenderOptions) => void }
}

let loader: Promise<void> | null = null

/** Loads the Infiteq booking widget script once and resolves when its API is ready. */
export function loadBookingWidget(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if ((window as BookingWindow).InfiteqBooking) return Promise.resolve()
  if (loader) return loader

  loader = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${WIDGET_SRC}"]`)
    const script = existing ?? document.createElement('script')
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => reject(new Error('Failed to load Infiteq booking widget')))
    if (!existing) {
      script.src = WIDGET_SRC
      script.async = true
      document.head.appendChild(script)
    }
  })

  return loader
}

/**
 * Mounts the booking widget into `target`. The widget only auto-boots once per page load,
 * so client-side navigation and modals have to render explicitly.
 */
export async function renderBooking(
  target: HTMLElement,
  opts: Omit<BookingRenderOptions, 'target' | 'slug'> & { slug?: string; maxWidth?: number } = {},
) {
  await loadBookingWidget()
  const api = (window as BookingWindow).InfiteqBooking
  if (!api) return
  api.render({
    target,
    slug: opts.slug ?? BOOKING_SLUG,
    meetingType: opts.meetingType,
    apiUrl: opts.apiUrl ?? BOOKING_API_URL,
    accent: opts.accent ?? BOOKING_ACCENT,
    theme: opts.theme,
  })
  applyMaxWidth(target, opts.maxWidth ?? BOOKING_MAX_WIDTH)
}

/**
 * The widget hard-codes `max-width: 720px` as an inline style on its root, inside a shadow root.
 * Only an `!important` rule injected into that shadow root can widen it.
 */
function applyMaxWidth(target: HTMLElement, maxWidth: number) {
  const root = target.shadowRoot
  if (!root || root.querySelector('style[data-infiteq-max-width]')) return
  const style = document.createElement('style')
  style.setAttribute('data-infiteq-max-width', '')
  style.textContent = `:host > div > div { max-width: ${maxWidth}px !important; }`
  root.appendChild(style)
}
